import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../../../stores/authStore";
import { useAdminContact } from "../../../core/api/hooks/useContact";
import { useAdminT } from "../../../hooks/useAdminT";
import { api } from "../../../lib/api";
import logo from "../../../assets/logo.png";
import {
    LayoutDashboard,
    Building2,
    Home,
    MapPin,
    Sparkles,
    MessageSquare,
    Mail,
    Users,
    Shield,
    FileText,
    Star,
    LogOut,
    ChevronRight,
    Bell,
    Settings,
    Search,
    PanelLeftClose,
    PanelLeftOpen,
    ChevronDown,
    Menu,
    X,
    BookOpen,
    Briefcase,
    LayoutTemplate,
    Globe,
} from "lucide-react";

interface NavItem {
    labelKey: string;
    path: string;
    icon: React.ComponentType<{ className?: string; strokeWidth?: number | string }>;
    badge?: number;
}

interface NavSection {
    labelKey: string;
    items: NavItem[];
}

const navSections: NavSection[] = [
    {
        labelKey: "nav.overview",
        items: [
            { labelKey: "nav.dashboard", path: "/admin", icon: LayoutDashboard },
        ],
    },
    {
        labelKey: "nav.propertyManagement",
        items: [
            { labelKey: "nav.projects", path: "/admin/projects", icon: Building2 },
            { labelKey: "nav.categories", path: "/admin/categories", icon: LayoutTemplate },
            { labelKey: "nav.units", path: "/admin/units", icon: Home },
            { labelKey: "nav.areas", path: "/admin/areas", icon: MapPin },
            { labelKey: "nav.amenities", path: "/admin/amenities", icon: Sparkles },
        ],
    },
    {
        labelKey: "nav.engagement",
        items: [
            { labelKey: "nav.inbox", path: "/admin/inbox", icon: Mail },
            { labelKey: "nav.inquiries", path: "/admin/inquiries", icon: MessageSquare },
            { labelKey: "nav.newsletter", path: "/admin/newsletter", icon: Mail },
        ],
    },
    {
        labelKey: "nav.accessControl",
        items: [
            { labelKey: "nav.users", path: "/admin/users", icon: Users },
            { labelKey: "nav.roles", path: "/admin/roles", icon: Shield },
        ],
    },
    {
        labelKey: "nav.content",
        items: [
            { labelKey: "nav.blog", path: "/admin/blog", icon: BookOpen },
            { labelKey: "nav.careers", path: "/admin/jobs", icon: Briefcase },
            { labelKey: "nav.banners", path: "/admin/cms/banners", icon: LayoutTemplate },
            { labelKey: "nav.pages", path: "/admin/cms/pages", icon: FileText },
            { labelKey: "nav.trustItems", path: "/admin/cms/trust-items", icon: Shield },
            { labelKey: "nav.siteSettings", path: "/admin/cms/settings", icon: Settings },

        ],
    },
];

// Breadcrumb segment → translation key mapping
const breadcrumbKeyMap: Record<string, string> = {
    "": "nav.dashboard",
    projects: "nav.projects",
    units: "nav.units",
    areas: "nav.areas",
    developers: "nav.developers",
    amenities: "nav.amenities",
    categories: "nav.categories",
    inquiries: "nav.inquiries",
    users: "nav.users",
    roles: "nav.roles",
    blog: "nav.blog",
    cms: "cms.title",
    banners: "nav.banners",
    featured: "nav.featuredSections",
    pages: "nav.pages",
    settings: "nav.siteSettings",
    jobs: "nav.careers",
    newsletter: "nav.newsletter",
    inbox: "nav.inbox",
};

interface DashboardLayoutProps {
    children?: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, lang, switchLanguage, isRtl } = useAdminT();

    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

    // ── Search state ──
    const [searchQuery, setSearchQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // ── Notifications state ──
    const [notifOpen, setNotifOpen] = useState(false);
    const notifRef = useRef<HTMLDivElement>(null);
    const { data: inboxData } = useAdminContact({ limit: 10, search: "" });
    const unreadMessages = (inboxData?.items ?? []).filter((m) => !m.read);
    const unreadCount = unreadMessages.length;

    // Flatten all nav items for search
    const allNavItems = navSections.flatMap(s => s.items);

    const searchResults = searchQuery.trim().length > 0
        ? allNavItems.filter(item =>
            t(item.labelKey).toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.path.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    // Ctrl/Cmd+K shortcut
    const handleGlobalKeyDown = useCallback((e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "k") {
            e.preventDefault();
            setSearchOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 10);
        }
        if (e.key === "Escape") {
            setSearchOpen(false);
            setNotifOpen(false);
            setSearchQuery("");
        }
    }, []);

    useEffect(() => {
        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, [handleGlobalKeyDown]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setSearchOpen(false);
                setSearchQuery("");
            }
            if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
                setNotifOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSearchSelect = (path: string) => {
        navigate(path);
        setSearchOpen(false);
        setSearchQuery("");
    };

    // Detect ID-like path segments (cuid/uuid shaped)
    const isId = (seg: string) => seg.length > 10 && !breadcrumbKeyMap[seg];

    // Extract project & floor IDs from path if present
    // Pattern: /admin/projects/:projectId/floors/:floorId
    const pathParts = location.pathname.split('/').filter(Boolean);
    const projectIdx = pathParts.indexOf('projects');
    const projectId = projectIdx >= 0 && isId(pathParts[projectIdx + 1] ?? '') ? pathParts[projectIdx + 1] : null;
    const floorsIdx = pathParts.indexOf('floors');
    const floorId = floorsIdx >= 0 && isId(pathParts[floorsIdx + 1] ?? '') ? pathParts[floorsIdx + 1] : null;

    // Fetch project name (only when an ID is in the path)
    const { data: projectData } = useQuery({
        queryKey: ['breadcrumb-project', projectId],
        queryFn: () => api.get(`/api/admin/projects/${projectId}`).then(r => r.data.data),
        enabled: !!projectId,
        staleTime: 60_000,
    });

    // Fetch floor name (only when a floor ID is in the path)
    const { data: floorData } = useQuery({
        queryKey: ['breadcrumb-floor', floorId],
        queryFn: () => api.get(`/api/admin/floors/${floorId}`).then(r => r.data.data),
        enabled: !!floorId,
        staleTime: 60_000,
    });

    // Resolve a project's localized title from its translations
    const resolveProjectName = (data: any) => {
        if (!data) return null;
        const trans = data.translations?.find((t: any) => t.locale === lang)
            || data.translations?.find((t: any) => t.locale === 'en')
            || data.translations?.[0];
        return trans?.title || data.slug || null;
    };

    // Compute breadcrumbs with translated labels
    const breadcrumbs = useMemo(() => {
        const parts = location.pathname.replace("/admin", "").split("/").filter(Boolean);
        if (parts.length === 0) return [{ label: t("nav.dashboard"), path: "/admin" }];
        return [
            { label: t("nav.dashboard"), path: "/admin" },
            ...parts.map((seg, i) => {
                let label: string;
                if (breadcrumbKeyMap[seg]) {
                    label = t(breadcrumbKeyMap[seg]);
                } else if (projectId && seg === projectId) {
                    label = resolveProjectName(projectData) || seg;
                } else if (floorId && seg === floorId) {
                    label = floorData?.name || seg;
                } else {
                    label = seg;
                }
                return { label, path: "/admin/" + parts.slice(0, i + 1).join("/") };
            }),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, lang, projectData, floorData]);

    // Close mobile sidebar on navigation
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const toggleSection = (key: string) => {
        setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const sidebarWidth = collapsed ? "w-[72px]" : "w-72";
    const sidebarSide = isRtl
        ? `fixed lg:relative inset-y-0 right-0 z-50`
        : `fixed lg:relative inset-y-0 left-0 z-50`;
    const mobileTranslate = isRtl
        ? (mobileOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0")
        : (mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0");

    return (
        <div className="flex h-screen bg-surface overflow-hidden" dir={isRtl ? "rtl" : "ltr"}>
            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    ${sidebarSide}
                    ${sidebarWidth} bg-secondary text-white flex flex-col
                    transition-all duration-300 ease-in-out
                    shadow-2xl
                    ${mobileTranslate}
                `}
            >
                {/* Logo Header */}
                <div className={`h-16 flex items-center border-b border-white/10 ${collapsed ? "justify-center px-3" : "px-6"}`}>
                    {collapsed ? (
                        <div className="w-9 h-9 bg-primary/30 rounded flex items-center justify-center">
                            <span className="font-display font-bold text-sm text-white">H</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 min-w-0">
                            <img src={logo} alt="HESN" className="h-8 w-auto flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-[10px] text-accent font-display uppercase tracking-[0.2em] truncate">
                                    {t("nav.adminPortal")}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 min-h-0 py-3 overflow-y-auto scrollbar-thin overflow-x-hidden">
                    {navSections.map((section) => (
                        <div key={section.labelKey} className="mb-1">
                            {/* Section Header */}
                            {!collapsed && (
                                <button
                                    onClick={() => toggleSection(section.labelKey)}
                                    className="w-full flex items-center justify-between px-5 py-2 group"
                                >
                                    <p className="text-[10px] font-display uppercase tracking-[0.2em] text-accent/50 group-hover:text-accent/80 transition-colors">
                                        {t(section.labelKey)}
                                    </p>
                                    <ChevronDown
                                        className={`w-3 h-3 text-accent/30 transition-transform duration-200 ${collapsedSections[section.labelKey] ? "-rotate-90" : ""}`}
                                    />
                                </button>
                            )}
                            {collapsed && <div className="py-1.5" />}

                            {/* Section Items */}
                            {!collapsedSections[section.labelKey] && (
                                <div className={`space-y-0.5 ${collapsed ? "px-2" : "px-3"}`}>
                                    {section.items.map((item) => (
                                        <NavLink
                                            key={item.path}
                                            to={item.path}
                                            end={item.path === "/admin"}
                                            title={collapsed ? t(item.labelKey) : undefined}
                                            className={({ isActive }) =>
                                                `group relative flex items-center gap-3 rounded-md transition-all duration-200 ${collapsed ? "justify-center p-2.5" : "px-3 py-2.5"
                                                } ${isActive
                                                    ? "bg-primary text-white shadow-lg shadow-primary/20"
                                                    : "hover:bg-white/[0.06] text-white/60 hover:text-white"
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    {/* Active indicator */}
                                                    {isActive && !collapsed && (
                                                        <span className={`absolute ${isRtl ? "right-0 rounded-l-full" : "left-0 rounded-r-full"} top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white/60`} />
                                                    )}
                                                    <item.icon
                                                        className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "text-white" : "text-accent/70 group-hover:text-white"}`}
                                                        strokeWidth={1.8}
                                                    />
                                                    {!collapsed && (
                                                        <>
                                                            <span className="text-[13px] font-medium flex-1 truncate">{t(item.labelKey)}</span>
                                                            {item.badge !== undefined && (
                                                                <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full font-semibold">
                                                                    {item.badge}
                                                                </span>
                                                            )}
                                                            {isActive && <ChevronRight className={`w-3.5 h-3.5 opacity-40 ${isRtl ? "rotate-180" : ""}`} />}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                {/* Collapse Toggle (desktop) */}
                <div className="hidden lg:block border-t border-white/10 p-2 flex-shrink-0">
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="w-full flex items-center justify-center gap-2 py-2 text-accent/60 hover:text-white hover:bg-white/[0.06] rounded-md transition-all"
                    >
                        {collapsed ? (
                            <PanelLeftOpen className="w-4 h-4" />
                        ) : (
                            <>
                                <PanelLeftClose className="w-4 h-4" />
                                <span className="text-xs">{t("nav.collapse")}</span>
                            </>
                        )}
                    </button>
                </div>

                {/* User Info */}
                <div className={`border-t border-white/10 bg-black/20 ${collapsed ? "p-2" : "p-4"} flex-shrink-0`}>
                    {collapsed ? (
                        <div className="flex flex-col items-center gap-2">
                            <button
                                onClick={() => navigate("/admin/profile")}
                                title="My Profile"
                                className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center font-display font-bold text-sm shadow-lg hover:ring-2 hover:ring-white/30 transition"
                            >
                                {user?.name?.charAt(0) || "A"}
                            </button>
                            <button
                                onClick={handleLogout}
                                title={t("nav.signOut")}
                                className="p-2 text-accent/60 hover:text-red-400 hover:bg-red-500/10 rounded-md transition"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => navigate("/admin/profile")}
                                className="flex flex-row items-center gap-3 mb-3 w-full text-start hover:bg-white/5 rounded-md px-2 py-2 -mx-2 -my-2 transition focus:outline-none"
                                title="My Profile"
                            >
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center font-display font-bold text-sm shadow-lg flex-shrink-0">
                                    {user?.name?.charAt(0) || "A"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{user?.name || "Admin"}</p>
                                    <p className="text-[11px] text-accent/70 truncate">{user?.role || "Administrator"}</p>
                                </div>
                            </button>
                            <button
                                onClick={handleLogout}
                                className="w-full py-2 text-sm font-medium flex items-center justify-center gap-2 bg-white/5 rounded-md hover:bg-red-500/20 hover:text-red-400 transition-all border border-white/[0.08]"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                {t("nav.signOut")}
                            </button>
                        </>
                    )}
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Top Header */}
                <header className="h-16 bg-white border-b border-accent/10 flex items-center justify-between px-4 lg:px-8 flex-shrink-0 shadow-sm z-20">
                    <div className="flex items-center gap-4">
                        {/* Mobile menu trigger */}
                        <button
                            onClick={() => setMobileOpen(!mobileOpen)}
                            className="lg:hidden p-2 hover:bg-surface rounded-md transition"
                        >
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5 text-text-muted" />}
                        </button>

                        {/* Breadcrumbs */}
                        <nav className="hidden md:flex items-center gap-1.5 text-sm">
                            {breadcrumbs.map((crumb, i) => (
                                <span key={crumb.path} className="flex items-center gap-1.5">
                                    {i > 0 && <ChevronRight className={`w-3.5 h-3.5 text-slate-300 ${isRtl ? "rotate-180" : ""}`} />}
                                    {i === breadcrumbs.length - 1 ? (
                                        <span className="font-medium text-secondary">{crumb.label}</span>
                                    ) : (
                                        <NavLink
                                            to={crumb.path}
                                            className="text-text-muted hover:text-primary transition-colors"
                                        >
                                            {crumb.label}
                                        </NavLink>
                                    )}
                                </span>
                            ))}
                        </nav>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Global Search */}
                        <div className="relative hidden sm:block" ref={searchRef}>
                            <div
                                className="flex items-center gap-2 pl-3 pr-3 py-2 bg-surface border border-accent/20 rounded-md w-52 xl:w-64 cursor-text hover:border-primary/30 transition-all"
                                onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 10); }}
                            >
                                <Search className="w-4 h-4 text-text-muted flex-shrink-0" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                                    onFocus={() => setSearchOpen(true)}
                                    placeholder={t("common.search")}
                                    className="bg-transparent text-sm outline-none flex-1 min-w-0 text-slate-700 placeholder:text-text-muted"
                                />
                                <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 border border-slate-200 rounded text-slate-400">
                                    ⌘K
                                </kbd>
                            </div>

                            {/* Search Dropdown */}
                            {searchOpen && searchResults.length > 0 && (
                                <div className="absolute top-full mt-1.5 left-0 right-0 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                                    {searchResults.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <button
                                                key={item.path}
                                                onClick={() => handleSearchSelect(item.path)}
                                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 hover:text-primary transition-colors text-left group"
                                            >
                                                <Icon className="w-4 h-4 text-slate-400 group-hover:text-primary flex-shrink-0" strokeWidth={1.8} />
                                                <span className="text-sm font-medium text-slate-700 group-hover:text-primary">{t(item.labelKey)}</span>
                                                <span className="ml-auto text-[11px] text-slate-400 font-mono truncate">{item.path}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {searchOpen && searchQuery.trim().length > 0 && searchResults.length === 0 && (
                                <div className="absolute top-full mt-1.5 left-0 right-0 bg-white rounded-xl shadow-xl border border-slate-200 z-50 px-4 py-3 text-sm text-slate-500">
                                    {t("common.noResults")}
                                </div>
                            )}
                        </div>

                        {/* Language Toggle */}
                        <button
                            onClick={() => switchLanguage(lang === "en" ? "ar" : "en")}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-semibold border border-accent/20 rounded-md hover:bg-surface hover:border-primary/30 transition-all text-text-muted hover:text-primary"
                            title={lang === "en" ? t("nav.switchToArabic") : t("nav.switchToEnglish")}
                        >
                            <Globe className="w-3.5 h-3.5" />
                            {lang === "en" ? "AR" : "EN"}
                        </button>

                        {/* Notifications */}
                        <div className="relative" ref={notifRef}>
                            <button
                                onClick={() => setNotifOpen(!notifOpen)}
                                className="p-2.5 rounded-md hover:bg-surface transition relative group"
                                title={t("nav.notifications")}
                            >
                                <Bell className="w-[18px] h-[18px] text-text-muted group-hover:text-primary transition-colors" />
                                {unreadCount > 0 && (
                                    <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-red-500 rounded-full ring-2 ring-white text-[9px] font-bold text-white px-0.5">
                                        {unreadCount > 9 ? "9+" : unreadCount}
                                    </span>
                                )}
                            </button>

                            {notifOpen && (
                                <div className="absolute top-full mt-2 end-0 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                                        <h4 className="font-semibold text-sm text-slate-800">{t("nav.notifications")}</h4>
                                        {unreadCount > 0 && (
                                            <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">
                                                {unreadCount} {t("nav.unread")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                                        {unreadMessages.length === 0 ? (
                                            <div className="px-4 py-8 text-center text-sm text-slate-400">
                                                {t("nav.noNotifications")}
                                            </div>
                                        ) : (
                                            unreadMessages.slice(0, 5).map((msg: any) => (
                                                <button
                                                    key={msg.id}
                                                    onClick={() => { navigate("/admin/inbox"); setNotifOpen(false); }}
                                                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors block"
                                                >
                                                    <p className="text-sm font-semibold text-slate-700 truncate">{msg.name}</p>
                                                    <p className="text-xs text-slate-400 truncate">{msg.message || msg.subject || msg.email}</p>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                    {unreadMessages.length > 0 && (
                                        <div className="border-t border-slate-100 p-2">
                                            <button
                                                onClick={() => { navigate("/admin/inbox"); setNotifOpen(false); }}
                                                className="w-full text-xs text-primary font-medium py-1.5 hover:bg-primary/5 rounded-lg transition-colors"
                                            >
                                                {t("nav.viewAllInbox")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Settings */}
                        <button
                            onClick={() => navigate("/admin/cms/settings")}
                            className="p-2.5 rounded-md hover:bg-surface transition group"
                            title={t("nav.siteSettings")}
                        >
                            <Settings className="w-[18px] h-[18px] text-text-muted group-hover:text-primary transition-colors" />
                        </button>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto bg-surface">
                    {children || <Outlet />}
                </main>
            </div>
        </div>
    );
}
